import React, { useState, useRef, useMemo } from 'react';
import ReactLoader from 'react-loading';
import { useHistory } from 'react-router-dom';

import moment from 'moment';
import { useTheme } from 'styled-components';
import { ThemeType } from 'theme/theme';

import Header from 'components/AppBar';
import DateRangePicker from 'components/DateRangePicker';
import EmptyList from 'components/EmptyList';
import ExpensesSelectableList, { Expense as ExpenseItem } from 'components/ExpensesSelectableList';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import SelectModal, { OptionsItem } from 'components/Selects/BookingSelectDate/components/Options';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { TravelType } from 'models/enums/travelType';
import { TravelExpensesResponse } from 'models/expense';
import ExpensesSuccess from 'screens/components/ExpensesSuccess';
import { getFormattedPrice } from 'utils/currency';

import {
  Container,
  Title,
  SubTitle,
  Body,
  LoadingContainer,
  Loading,
  CustomSelectContainer,
  CustomSelectContainerEmpty,
  EmptyListContainer,
  ImportButtonContainer,
  ImportButton,
} from './styles';

interface Props {
  isLoadingRappi: boolean;
  isLoadingTravel: boolean;
  isImporting: boolean;
  travelId: string;
  travel?: TravelExpensesResponse;
  expenses?: ExpenseItem[];
  currentExpenses?: string[];
  totalExpenses: number;
  initialDate: Date;
  finalDate: Date;
  currency: string;
  onSelect: (id: ExpenseItem) => void;
  onChangeDate: (dates: { initialDate?: Date; finalDate?: Date }) => void;
  onImport: () => void;
  showSuccess: boolean;
}

const ExpensesListImport = ({
  isLoadingRappi,
  isLoadingTravel,
  isImporting,
  travelId,
  expenses,
  travel,
  currentExpenses,
  totalExpenses,
  initialDate,
  finalDate,
  currency,
  onChangeDate,
  onSelect,
  onImport,
  showSuccess,
}: Props) => {
  const history = useHistory();
  const [show, setShow] = useState(false);
  const [period, setPeriod] = useState('default');
  const [filterStartDate, setFilterStartDate] = useState<Date>();
  const [filterEndDate, setFilterEndDate] = useState<Date>();
  const periodRef = useRef(null);
  const theme = useTheme() as ThemeType;

  const { t } = useStoreTranslation();
  const formatDate = (date?: Date) => {
    if (!date) return '';
    return moment(date).format(t('expensesImportRappi.dateFormat'));
  };

  const handleSelect = (expense: ExpenseItem) => {
    onSelect(expense);
  };

  const periodItems: OptionsItem[] = [
    {
      text: `${moment(initialDate).format('ddd[,] DD MMM')} - ${moment(finalDate).format('ddd[,] DD MMM')}`,
      value: 'default',
    },
    {
      text: t('expensesImportRappi.last7days'),
      value: 'last7days',
    },
    {
      text: t('expensesImportRappi.last30days'),
      value: 'last30days',
    },
    { text: t('expensesImportRappi.other'), value: 'date' },
  ];

  const handleChangeDateSelect = (dates: { initialDate: Date; finalDate: Date }) => {
    setPeriod('');
    setFilterStartDate(dates?.initialDate);
    setFilterEndDate(dates?.finalDate);
    onChangeDate(dates);
  };

  const handleChangePeriod = (value: string) => {
    let start: Date = new Date();
    let end: Date = new Date();
    if (value === 'date') {
      setPeriod('date');
      return setShow(true);
    }
    if (value === 'last30days') {
      start = moment().subtract('30', 'days').toDate();
      end = moment().toDate();
    }
    if (value === 'last7days') {
      start = moment().subtract('7', 'days').toDate();
      end = moment().toDate();
    }
    if (value === 'default') {
      start = moment(initialDate).add(-1, 'days').toDate();
      end = moment(finalDate).add(1, 'days').toDate();
    }

    setPeriod(value);
    setFilterStartDate(start);
    setFilterEndDate(end);
    onChangeDate({ initialDate: start, finalDate: end });
  };

  const handlerImport = () => {
    onImport();
  };

  const makeVale = (key: string) => periodItems.find((item) => item.value === key)?.text;

  const formattedFilterDate = useMemo(() => {
    if (filterStartDate && filterEndDate) {
      return `${moment(filterStartDate).format('ddd[,] DD MMM')} - ${moment(filterEndDate).format('ddd[,] DD MMM')}`;
    }
  }, [filterStartDate, filterEndDate]);

  const currencyValue = useMemo(() => {
    const formattedPrice = (value: number | undefined) => {
      const { formatted } = getFormattedPrice({ value, currency, t });
      return formatted;
    };
    return formattedPrice(totalExpenses);
  }, [currency, totalExpenses, t]);

  const subtitle = `${t(
    travel?.type === TravelType.Vehicle
      ? 'expensesImportRappi.vehicleTitle'
      : travel?.type === TravelType.Hotel
      ? 'expensesImportRappi.hotelTitle'
      : travel?.type === TravelType.ExtraCharge
      ? 'expensesImportRappi.extraChargeTitle'
      : 'expensesImportRappi.flightTitle',
    {
      city: travel?.destinationName,
    },
  )} | ${formatDate(travel?.checkin)}`;

  return isLoadingTravel ? (
    <LoadingContainer>
      <Loading fullScreen />
    </LoadingContainer>
  ) : (
    <>
      <Container>
        <Mobile>
          <Header goBack={() => history.goBack()}>
            <Title>{t('expensesImportRappi.title')}</Title>
            <SubTitle>{subtitle}</SubTitle>
          </Header>
        </Mobile>

        <Desktop>
          <ScreenHeader title={t('expensesImportRappi.title')} subtitle={subtitle} />
        </Desktop>

        <Body grey={expenses?.length === 0 || !expenses}>
          {expenses?.length === 0 || !expenses ? (
            <>
              <CustomSelectContainerEmpty>
                <CustomSelect
                  name="period"
                  label={t('expensesImportRappi.selectPeriod')}
                  onChange={handleChangePeriod}
                  placeHolder={t('expensesImportRappi.selectPeriod')}
                  selectRef={periodRef}
                  value={period ? makeVale(period) : formattedFilterDate}
                  disabled={isLoadingRappi}
                >
                  {({ ...selectProps }: CustomSelectRenderProps) => (
                    <SelectModal {...selectProps} selectRef={periodRef} items={periodItems} />
                  )}
                </CustomSelect>
              </CustomSelectContainerEmpty>
              {isLoadingRappi ? (
                <LoadingContainer>
                  <ReactLoader type="spin" color={theme.loading} width={60} height={60} />
                </LoadingContainer>
              ) : (
                <EmptyListContainer>
                  <EmptyList
                    grey
                    title={t('expensesImportRappi.emptyTitle')}
                    description=""
                    image="/corporate/images/receipt-list-43-colored-48.svg"
                    backgroundImage="/corporate/images/group-3.svg"
                  />
                </EmptyListContainer>
              )}
            </>
          ) : (
            <>
              <CustomSelectContainer>
                <CustomSelect
                  name="period"
                  label={t('expensesImportRappi.selectPeriod')}
                  onChange={handleChangePeriod}
                  placeHolder={t('expensesImportRappi.selectPeriod')}
                  selectRef={periodRef}
                  value={period ? makeVale(period) : formattedFilterDate}
                  disabled={isLoadingRappi}
                >
                  {({ ...selectProps }: CustomSelectRenderProps) => (
                    <SelectModal {...selectProps} selectRef={periodRef} items={periodItems} />
                  )}
                </CustomSelect>
              </CustomSelectContainer>
              {isLoadingRappi ? (
                <LoadingContainer>
                  <ReactLoader type="spin" color={theme.loading} width={60} height={60} />
                </LoadingContainer>
              ) : (
                <ExpensesSelectableList
                  expenses={expenses || []}
                  currentExpenses={currentExpenses || []}
                  isSelectable={true}
                  onSelect={handleSelect}
                />
              )}
            </>
          )}
          {currentExpenses?.length !== 0 && (
            <ImportButtonContainer>
              <ImportButton loading={isImporting} disabled={isImporting} onClick={handlerImport}>
                {currentExpenses?.length === 1 ? (
                  <ImportButton.Label>{t('expensesImportRappi.importExpense')}</ImportButton.Label>
                ) : (
                  <ImportButton.Label>
                    {t('expensesImportRappi.importExpenses', { number: currentExpenses?.length })}
                  </ImportButton.Label>
                )}
                <ImportButton.Total>
                  {t('expensesImportRappi.total')} - {currencyValue}
                </ImportButton.Total>
              </ImportButton>
            </ImportButtonContainer>
          )}
        </Body>
      </Container>

      <DateRangePicker
        allDate
        currentInitialDate={filterStartDate}
        currentFinalDate={filterEndDate}
        onlyDate={false}
        show={show}
        onChange={({ initialDate, finalDate }) =>
          handleChangeDateSelect({ initialDate, finalDate: finalDate || new Date() })
        }
        onClose={() => setShow(false)}
      />

      <ExpensesSuccess show={showSuccess} travelId={travelId} />
    </>
  );
};

export default ExpensesListImport;
